import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useState, useCallback, useMemo } from "react";
import { DosePrescription } from "./SiteTable.types";
import {
  getDoses,
  getDoseFractions,
  doseLabels,
} from "@utils/doseAndFractions";
import SiteNameModal from "@pages/Order/components/SiteNameModal/SiteNameModal";
import {
  TextFieldCell,
  AutocompleteCell,
  TreatmentSiteCell,
  ActionCell,
  DoseCell,
} from "@components/TableComponents/TableComponents";
import { useTheme } from "@mui/material/styles";

interface SiteTableProps {
  dataTestid: string;
  isReadOnly: boolean;
  dosePrescriptions: DosePrescription[];
  treatmentSiteOptions: string[];
  lateralityOptions: string[];
  techniqueOptions: string[];
  sequenceOptions: string[];
  isodoseOptions: string[];
  frequencyOptions: string[];
  energyOptions: string[];
  doseUnit: string;
  fractionUnit: string;
  addSite: (name: string, nameSpecify?: string) => void;
  removeSite: (id: string | number) => void;
  updateSite: (site: DosePrescription) => boolean;
  submitCount: number;
}

const SiteTable = ({
  dataTestid,
  isReadOnly,
  dosePrescriptions,
  treatmentSiteOptions,
  lateralityOptions,
  techniqueOptions,
  sequenceOptions,
  isodoseOptions,
  frequencyOptions,
  energyOptions,
  doseUnit,
  fractionUnit,
  addSite,
  removeSite,
  updateSite,
}: SiteTableProps) => {
  const [showAddSite, setShowAddSite] = useState(false);
  const theme = useTheme();

  const handleUpdateSite = useCallback(
    (site: DosePrescription, field: string, value: string | number[]) => {
      const updatedSite = { ...site, [field]: value };
      updateSite(updatedSite);
    },
    [updateSite],
  );

  const handleAddSite = (name: string, nameSpecify: string) => {
    addSite(name, nameSpecify);
    setShowAddSite(false);
  };

  const hasErrors = useMemo(
    () =>
      dosePrescriptions.some(
        (row) =>
          row.errors &&
          row.errors.length > 0 &&
          row.touched &&
          row.touched.some((field) => row.errors && row.errors.includes(field)),
      ),
    [dosePrescriptions],
  );

  return (
    <Card
      data-testid={dataTestid}
      style={{ marginLeft: "2px", marginRight: "2px" }}
    >
      <CardHeader
        title="Dose Prescription"
        slotProps={{
          title: {
            variant: "subtitle1",
          },
        }}
      />
      {hasErrors && <Alert severity="error">Missing required fields</Alert>}
      <CardContent style={{ paddingTop: 0 }}>
        <TableContainer component={Box}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ padding: 1, width: "10%" }}>
                  Treatment Site
                </TableCell>
                <TableCell sx={{ padding: 1, width: "10%" }}>
                  Laterality
                </TableCell>
                <TableCell sx={{ padding: 1, width: "10%" }}>
                  Technique
                </TableCell>
                <TableCell sx={{ padding: 1, width: "10%" }}>Energy</TableCell>
                <TableCell sx={{ padding: 1, width: "14%" }}>
                  Dose ({doseUnit})
                </TableCell>
                <TableCell sx={{ padding: 1, width: "8%" }}>
                  Fraction ({fractionUnit})
                </TableCell>
                <TableCell sx={{ padding: 1, width: "14%" }}>
                  Dose ({doseUnit}/{fractionUnit})
                </TableCell>
                <TableCell sx={{ padding: 1, width: "15%" }}>
                  Prescription PT/Isodose
                </TableCell>
                <TableCell sx={{ padding: 1, width: "8%" }}>Sequence</TableCell>
                <TableCell sx={{ padding: 1, width: "12%" }}>
                  Frequency
                </TableCell>
                {!isReadOnly && <TableCell sx={{ padding: 1, width: "1%" }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {dosePrescriptions.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TreatmentSiteCell
                    value={row.name || ""}
                    nameSpecify={row.nameSpecify || ""}
                    options={treatmentSiteOptions}
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="name"
                    onUpdate={(name, nameSpecify) => {
                      const updatedSite = {
                        ...row,
                        name: name,
                        nameSpecify: nameSpecify,
                      };
                      updateSite(updatedSite);
                    }}
                  />
                  <AutocompleteCell
                    value={row.laterality || ""}
                    options={lateralityOptions}
                    onChange={(value) =>
                      handleUpdateSite(row, "laterality", value)
                    }
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="laterality"
                  />
                  <AutocompleteCell
                    value={row.technique || ""}
                    options={techniqueOptions}
                    onChange={(value) =>
                      handleUpdateSite(row, "technique", value)
                    }
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="technique"
                  />
                  <AutocompleteCell
                    value={row.energy || ""}
                    options={energyOptions}
                    onChange={(value) => handleUpdateSite(row, "energy", value)}
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="energy"
                  />
                  <DoseCell
                    value={getDoses(row).join("-")}
                    onChange={(value) => handleUpdateSite(row, "dose", value)}
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="dose"
                    doseUnit={doseUnit}
                    doses={getDoses(row)}
                    onDosesChange={(doses) => {
                      const updatedSite = { ...row };
                      if (doses.length === 1) {
                        updatedSite.highDose = doses[0];
                        updatedSite.mid1Dose = null;
                        updatedSite.mid2Dose = null;
                        updatedSite.lowDose = null;
                      } else if (doses.length === 2) {
                        updatedSite.highDose = doses[0];
                        updatedSite.lowDose = doses[1];
                        updatedSite.mid1Dose = null;
                        updatedSite.mid2Dose = null;
                      } else if (doses.length === 3) {
                        updatedSite.highDose = doses[0];
                        updatedSite.mid1Dose = doses[1];
                        updatedSite.lowDose = doses[2];
                        updatedSite.mid2Dose = null;
                      } else if (doses.length === 4) {
                        updatedSite.highDose = doses[0];
                        updatedSite.mid1Dose = doses[1];
                        updatedSite.mid2Dose = doses[2];
                        updatedSite.lowDose = doses[3];
                      }
                      handleUpdateSite(updatedSite, "dose", doses);
                    }}
                  />
                  <TextFieldCell
                    value={row.fractions?.toString() || ""}
                    onChange={(value) =>
                      handleUpdateSite(row, "fractions", value)
                    }
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="fractions"
                  />
                  <TableCell sx={{ padding: 1 }}>
                    {row.fractions
                      ? getDoseFractions(row.fractions, getDoses(row))
                          .length === 1
                        ? getDoseFractions(row.fractions, getDoses(row))[0]
                        : getDoseFractions(row.fractions, getDoses(row)).map(
                            (dose, index) => (
                              <Typography key={index} variant="body2">
                                {doseLabels[getDoses(row).length][index]}:{" "}
                                {dose}
                              </Typography>
                            ),
                          )
                      : ""}
                  </TableCell>
                  <AutocompleteCell
                    value={row.isodose || ""}
                    options={isodoseOptions}
                    onChange={(value) =>
                      handleUpdateSite(row, "isodose", value)
                    }
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="isodose"
                  />
                  <AutocompleteCell
                    value={row.sequence || ""}
                    options={sequenceOptions}
                    onChange={(value) =>
                      handleUpdateSite(row, "sequence", value)
                    }
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="sequence"
                  />
                  <AutocompleteCell
                    value={row.frequency || ""}
                    options={frequencyOptions}
                    onChange={(value) =>
                      handleUpdateSite(row, "frequency", value)
                    }
                    isReadOnly={isReadOnly}
                    errors={row.errors}
                    field="frequency"
                  />
                  {!isReadOnly && (
                    <ActionCell onDelete={() => removeSite(row.id)} />
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!isReadOnly && (
          <Button
            onClick={() => setShowAddSite(true)}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Add Treatment Site
          </Button>
        )}
      </CardContent>
      <SiteNameModal
        open={showAddSite}
        options={treatmentSiteOptions}
        onSubmit={handleAddSite}
        onDismiss={() => setShowAddSite(false)}
      />
    </Card>
  );
};

export default SiteTable;
